import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, Typography } from '@mui/material';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { MonetizableEnum } from '@repo/segment/enums';
import type { AnalyticsType } from '@repo/segment/types';
import type { MarketplaceRateTableTabName } from '@repo/supergraph/structuredContent/hydration/MarketplaceRateTable/constants';
import { CtaButton } from '@repo/ui/CtaButton';
import { DisclosureTooltip } from '@repo/ui/DisclosureTooltip';
import { getRateTableCta } from './cta';
import {
  getDrivers,
  type RateTableDriver,
  type RateTableEntity,
  VALUE_FONT_SIZE_BY_TAB,
} from './drivers';
import { RateTableLogo } from './RateTableLogo';

export const IMPRESSION_SECTION = 'marketplace_rate_table';

const LEFT_COLUMN_WIDTH = 205;

type Props = {
  entity: RateTableEntity;
  tabName: MarketplaceRateTableTabName;
  position: number;
};

function DriverColumn({
  driver,
  tabName,
}: {
  driver: RateTableDriver;
  tabName: MarketplaceRateTableTabName;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <Typography component="p" variant="textSmall" sx={{ color: 'grey.400' }}>
        {driver.label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          component="p"
          sx={{ fontSize: VALUE_FONT_SIZE_BY_TAB[tabName], lineHeight: 1.3 }}
        >
          {driver.value}
        </Typography>
        {driver.valueTooltip && (
          <DisclosureTooltip
            trigger={
              <Link aria-label={`${driver.label} info`} component="button" color="text.primary">
                <HelpOutlineIcon sx={{ width: 14, height: 14 }} />
              </Link>
            }
          >
            <Typography variant="textSmall">{driver.valueTooltip}</Typography>
          </DisclosureTooltip>
        )}
      </Box>
    </Box>
  );
}

export function RateTableProductCard({ entity, tabName, position }: Props) {
  const { displayName, image, institution, product } = entity;
  const name = displayName ?? entity.name;
  const cta = getRateTableCta(entity);
  // segment and supergraph each declare an identical MonetizableEnum.
  const monetizable = (entity.monetizable ?? MonetizableEnum.no) as MonetizableEnum;

  const ctaAnalytics: AnalyticsType = {
    index: position,
    institutionId: institution?.id,
    monetizable,
    offerId: entity.id,
    productId: product?.id,
    productType: entity.productType,
  };

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={name}
      productType={entity.productType}
      level="offer"
      institutionId={institution?.id}
      institutionName={institution?.name}
      productId={product?.id}
      productName={product?.name}
      offerId={entity.id}
      offerName={entity.name}
      monetizable={monetizable}
      impressionSection={IMPRESSION_SECTION}
      impressionPosition={position}
    >
      <Box
        data-testid="rate-table-product-card"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          padding: {
            xs: 2,
            md: 3,
          },
          border: '1px solid',
          borderColor: 'neutral.lighter',
          backgroundColor: 'common.white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            order: { xs: 2, md: 1 },
            width: { xs: '100%', md: LEFT_COLUMN_WIDTH },
            flexShrink: 0,
          }}
        >
          <RateTableLogo
            ctaAnalytics={ctaAnalytics}
            cta={cta}
            image={image}
            displayName={name}
            width={140}
            height={44}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          />
          <Box sx={{ width: '100%' }}>
            <CtaButton
              cta={cta}
              analyticsPayload={ctaAnalytics}
              impressionSectionOverride={IMPRESSION_SECTION}
              subtextStylesOverride={{ color: 'neutral.darker' }}
              fullWidth
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            order: { xs: 1, md: 2 },
            flex: 1,
            paddingTop: { md: 1 },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography component="p" variant="textSmall" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>
            <RateTableLogo
              ctaAnalytics={ctaAnalytics}
              cta={cta}
              image={image}
              displayName={name}
              width={80}
              height={32}
              sx={{ display: { xs: 'flex', md: 'none' }, width: 'auto', flexShrink: 0 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {getDrivers(tabName, entity).map(driver => (
              <DriverColumn key={driver.label} driver={driver} tabName={tabName} />
            ))}
          </Box>
        </Box>
      </Box>
    </AnalyticsSkuViewed>
  );
}
